import React, { FC, useState } from "react";
import MinusIcon from "../../../../../images/products/minus-blue.svg";
import Plus from "../../../../../images/products/plus.svg";
import { observer } from "mobx-react";
import {
  CustomerFacingCheckOutItemModel,
  getPriceByBillingType,
  MediaModel,
  MerchantAccount,
  MerchantAccountWithPaySettingsModel,
  ProductAttributeInputModel,
  ProductAttributeMappingsInput,
  ProductAttributeWithOption,
  PurchaseCheckOutAddOnDetails,
  PurchasePaymentModel,
  RelatedItemsCategoriesModel,
  RelatedItemsPriceCategoriesModel,
} from "../models";
import { AttributeValueTypes } from "../enums";
import {
  CustomLabel,
  FlexContainer,
  IOption,
  SingleSelect,
} from "@mindmemobile/mm-shared-global";
import { CustomLine, SelectedColorButton } from "../styles";
import ImgPlaceholder from "../../../../../images/placeholders/no-image.svg";
import { AccountSettingsStore } from "../store";
import { PromotionModel } from "../models/PromotionModels";

type IProps = {
  items: CustomerFacingCheckOutItemModel[];
  addOns: PurchaseCheckOutAddOnDetails[];
  updateItems: (items: CustomerFacingCheckOutItemModel[]) => void;
  updateAddOns: (addOns: PurchaseCheckOutAddOnDetails[]) => void;
  paymentSectionData: PurchasePaymentModel;
  merchantAccount: MerchantAccountWithPaySettingsModel;
  /* Rate this Checkout / Purchase Link was saved with, not the merchant
     account's current default. */
  surchargePercentage?: number;
  includeSurcharge: boolean;
};

const PurchasePaymentItemSection: FC<IProps> = ({
  items,
  addOns,
  updateItems,
  updateAddOns,
  paymentSectionData,
  merchantAccount,
  surchargePercentage = 0,
  includeSurcharge,
}) => {
  const [remainingDiscountAmount, setRemainingDiscountAmount] = useState(
    paymentSectionData.promotionRelatedData?.maximumAmount || 0,
  );
  const clonedItems = items.map(
    (item) =>
      new CustomerFacingCheckOutItemModel({
        ...item,
        selectedVariantIndex:
          item.selectedVariantIndex === -1 ? 0 : item.selectedVariantIndex,
      }),
  );
  const sampleOptions: IOption[] = [{ label: "USD", value: "USD" }];
  const options = [sampleOptions];

  function onQuantityChange(
    index: number,
    isIncrement: boolean,
    isAddOn: boolean,
  ) {
    if (isAddOn) {
      const clonedItems = addOns.map(
        (item) => new PurchaseCheckOutAddOnDetails(item),
      );
      const item = clonedItems[index];
      if (isIncrement) {
        item.quantity++;
      } else {
        item.quantity--;
      }
      // if (
      //   item.inventorySettings &&
      //   item.inventorySettings.isLimitPurchaseQuantity &&
      //   item.quantity > item.inventorySettings?.quantityLimit
      // ) {
      //   return;
      // }
      updateAddOns(clonedItems);
    } else {
      const clonedItems = items.map(
        (item) => new CustomerFacingCheckOutItemModel(item),
      );
      const item = clonedItems[index];
      if (isIncrement) {
        item.quantity++;
      } else {
        item.quantity--;
      }
      if (
        item.inventorySettings &&
        item.inventorySettings.isLimitPurchaseQuantity &&
        item.quantity > item.inventorySettings?.quantityLimit
      ) {
        return;
      }
      updateItems(clonedItems);
    }
  }

  function getFormattedVariantData(
    variants: ProductAttributeInputModel,
    selectedVariantIndex: number,
  ) {
    const selectedVariant: ProductAttributeMappingsInput =
      variants.options[selectedVariantIndex];
    const formattedData = selectedVariant.attributeMappings.map(
      (attribute: ProductAttributeWithOption) => {
        if (attribute.valueType === AttributeValueTypes.Color) {
          return {
            attributeName: attribute.attributeName,
            optionName: attribute.optionName,
            color: attribute.color?.rgba,
          };
        } else {
          return {
            attributeName: attribute.attributeName,
            optionName: attribute.optionName,
          };
        }
      },
    );

    return formattedData;
  }

  const renderItemDetails = (
    index: number,
    isAddOn: boolean,
    itemType: string,
    medias: MediaModel[],
    name: string,
    description: string,
    quantity: number,
    hasVariants: boolean,
    variants: ProductAttributeInputModel,
    selectedVariantIndex: number,
    price: RelatedItemsPriceCategoriesModel,
    checkOutModel?: CustomerFacingCheckOutItemModel,
    addOnModel?: PurchaseCheckOutAddOnDetails,
  ) => {
    const itemMedia = isAddOn
      ? addOnModel?.getAddOnMedia()
      : checkOutModel?.getItemMedia;

    const media = itemMedia && itemMedia?.length > 0 ? itemMedia[0] : null;
    const formattedData =
      hasVariants && variants.options.length > 0
        ? getFormattedVariantData(variants, selectedVariantIndex)
        : [];

    const customAmount =
      checkOutModel?.customPricingInputValue ||
      addOnModel?.customPricingInputValue;
    const priceDescription = getPriceByBillingType(
      itemType,
      hasVariants,
      variants,
      price.id,
      price.billingType,
      price.terms,
      price.recurrence,
      quantity,
      selectedVariantIndex,
      customAmount ? parseFloat(customAmount) : undefined,
      // No per-item flag any more: the checkout/link setting alone decides.
      includeSurcharge ? surchargePercentage : 0,
      undefined,
    );
    return (
      <>
        <FlexContainer key={index} className="product-item" spacing={20}>
          {media ? (
            media.type === "Video" ? (
              <video src={media.fullURL} />
            ) : (
              <img src={media?.fullURL} alt="img" />
            )
          ) : (
            <img src={ImgPlaceholder} alt="img" />
          )}
          <div className="flex-auto-grow">
            <FlexContainer className="product-description">
              <CustomLabel>{name || description}</CustomLabel>
              {hasVariants && (
                <div className="width-inherit">
                  <FlexContainer alignItems="center" className="attribute-flex">
                    {formattedData.map((attribute, index) => (
                      <div key={attribute.attributeName}>
                        <FlexContainer
                          className="attribute-container"
                          alignItems="center"
                          spacing={6}
                        >
                          {attribute.color ? (
                            <>
                              <SelectedColorButton
                                heightWidth="12px"
                                padding="0px"
                                borderRadius="2px"
                                key={index}
                                color={attribute.color}
                                className="color-picker-gallery"
                              >
                                <div />
                              </SelectedColorButton>
                            </>
                          ) : null}
                          <CustomLabel
                            fontSize={"10px"}
                            fontWeight="400"
                            lineHeight="14px"
                            color="#555 !important"
                          >
                            {attribute.optionName}
                          </CustomLabel>
                          {index < formattedData.length - 1 && (
                            <CustomLabel fontSize="10px">|</CustomLabel>
                          )}
                        </FlexContainer>
                      </div>
                    ))}
                  </FlexContainer>
                </div>
              )}

              {itemType !== "Donation" && (
                <div>
                  <FlexContainer
                    className="product-quantity"
                    // margin="10px 0px 0px"
                    margin={
                      formattedData.some((attr) => attr.color)
                        ? "6px 0px 0px"
                        : "12px 0px 0px"
                    }
                  >
                    {quantity > 1 && (
                      <img
                        onClick={() => onQuantityChange(index, false, isAddOn)}
                        src={MinusIcon}
                        alt="Delete Icon"
                      />
                    )}
                    <input type={"text"} value={quantity} disabled={true} />

                    <img
                      src={Plus}
                      alt="Add"
                      onClick={() => onQuantityChange(index, true, isAddOn)}
                    />
                  </FlexContainer>
                </div>
              )}
            </FlexContainer>
          </div>
          <div>
            {priceDescription?.discount ? (
              <FlexContainer
                alignItems="center"
                justifyContent="flex-end"
                spacing={10}
              >
                <CustomLabel
                  className="discounted-price"
                  style={{
                    color: "red",
                    fontSize: "12px",
                    textDecoration: "line-through",
                  }}
                >
                  $
                  {AccountSettingsStore.formatAmount(
                    priceDescription?.subTotal || 0,
                    true,
                  )}
                </CustomLabel>
                <CustomLabel className="discounted-price">
                  $
                  {AccountSettingsStore.formatAmount(
                    priceDescription?.totalPriceWithDiscountSurcharge || 0,
                    true,
                  )}
                </CustomLabel>
              </FlexContainer>
            ) : (
              <FlexContainer alignItems="center" justifyContent="flex-end">
                <CustomLabel className="discounted-price">
                  $
                  {AccountSettingsStore.formatAmount(
                    priceDescription?.subTotal || 0,
                    true,
                  )}
                </CustomLabel>
              </FlexContainer>
            )}
          </div>
        </FlexContainer>
        <CustomLine
          width="100%"
          height="2px"
          bgColor="#ddd"
          margin="14px 0px"
        />
      </>
    );
  };

  function onIncludedItemsQuantityChange(
    itemIndex: number,
    includedIndex: number,
    isIncrement: boolean,
  ) {
    const clonedItems = items.map(
      (item) => new CustomerFacingCheckOutItemModel(item),
    );
    const clonedAddOns =
      addOns.map((addOn) => new PurchaseCheckOutAddOnDetails(addOn)) || [];
    const parentItem = clonedItems[itemIndex];
    //  if (parentItem.includedItems) {
    const includedItems =
      parentItem.includedItemsByCondition?.map(
        (ic) => new RelatedItemsCategoriesModel(ic),
      ) || [];
    // parentItem.getAllowedIncludedItems()?.map(
    //   (incItem) =>
    //     new RelatedItemsCategoriesModel({
    //       ...incItem,
    //     })
    // ) || [];
    const includedItem = includedItems[includedIndex];
    let quantity = parseInt(includedItem.quantity, 10) || 0;
    if (isIncrement) {
      quantity++;
    } else if (quantity > 1) {
      quantity--;
    }
    includedItem.quantity = quantity.toString();
    parentItem.includedItemsByCondition = includedItems;
    if (quantity > 1) {
      const addOnIndex = clonedAddOns.findIndex(
        (addOn) => addOn.itemId === includedItem.id,
      );
      if (addOnIndex === -1) {
        let selectedVariantId = "";
        let onlyAllowedVariantIds: string[] = [];
        if (includedItem.hasVariants) {
          const validRequiredVariants =
            includedItem.getValidRequiredVariantOptions();
          selectedVariantId =
            validRequiredVariants.length > 0 ? validRequiredVariants[0].id : "";
          onlyAllowedVariantIds = includedItem.requiredVariantIds;
        }
        const newAddOn = new PurchaseCheckOutAddOnDetails({
          ...includedItem.prices[0],
          itemId: includedItem.id,
          itemType: includedItem.type,
          variantId: selectedVariantId,
          selectedVariant: selectedVariantId
            ? includedItem.variants
            : undefined,
          hasVariants: includedItem.hasVariants,
          onlyAllowedVariantIds: onlyAllowedVariantIds,
          name: includedItem.name,
          description: includedItem.description,
          descriptionEditorState: includedItem.descriptionEditorState,
          media: includedItem.medias,
          descriptionEditorRawContent: includedItem.descriptionEditorRawContent,
          isTrackInventory: includedItem.isTrackInventory,
          quantity: quantity - 1,
          priceId: includedItem.selectedPriceIds[0],
          isIncludedItem: true,
          hasSurcharge: includedItem.surcharge,
        });
        clonedAddOns.push(newAddOn);
      } else {
        const existingAddOn = clonedAddOns[addOnIndex];
        existingAddOn.quantity = quantity - 1;
      }
    } else {
      const addOnIndex = clonedAddOns.findIndex(
        (addOn) => addOn.itemId === includedItem.id,
      );
      if (addOnIndex !== -1) {
        clonedAddOns.splice(addOnIndex, 1);
      }
    }
    //}
    updateItems(clonedItems);
    updateAddOns(clonedAddOns);
  }

  const renderIncludedItems = (
    item: RelatedItemsCategoriesModel,
    itemIndex: number,
    includedIndex: number,
  ) => {
    const {
      medias,
      name,
      description,
      hasVariants,
      quantity,
      variants,
      type,
      prices,
    } = item;
    const media = medias.length > 0 ? medias[0] : null;
    const price =
      prices.find((price) => price.id === item.selectedPriceIds[0]) ||
      new RelatedItemsPriceCategoriesModel();
    const formattedData =
      hasVariants && variants.options.length > 0
        ? getFormattedVariantData(variants, 0)
        : [];

    const parsedQuantity = parseInt(quantity);
    const priceDescription = getPriceByBillingType(
      type,
      hasVariants,
      variants,
      price.id,
      price.billingType,
      price.terms,
      price.recurrence,
      parsedQuantity > 1 ? parsedQuantity - 1 : parsedQuantity,
      0,
      undefined,
      // No per-item flag any more: the checkout/link setting alone decides.
      includeSurcharge ? surchargePercentage : 0,
      paymentSectionData.promotionRelatedData || undefined,
    );
    return (
      <>
        <FlexContainer key={item.id} className="product-item" spacing={20}>
          {media ? (
            media.type === "Video" ? (
              <video src={media.fullURL} />
            ) : (
              <img src={media?.fullURL} alt="img" />
            )
          ) : (
            <img src={ImgPlaceholder} alt="img" />
          )}
          <div className="flex-auto-grow">
            <FlexContainer className="product-description">
              <CustomLabel>{name || description}</CustomLabel>
              {hasVariants && (
                <div className="width-inherit">
                  <FlexContainer alignItems="center" className="attribute-flex">
                    {formattedData.map((attribute, index) => (
                      <div key={attribute.attributeName}>
                        <FlexContainer
                          className="attribute-container"
                          alignItems="center"
                          spacing={6}
                        >
                          {attribute.color ? (
                            <>
                              <SelectedColorButton
                                heightWidth="12px"
                                padding="0px"
                                borderRadius="2px"
                                key={index}
                                color={attribute.color}
                                className="color-picker-gallery"
                              >
                                <div />
                              </SelectedColorButton>
                            </>
                          ) : null}
                          <CustomLabel
                            fontSize={"10px"}
                            fontWeight="400"
                            lineHeight="14px"
                            color="#555 !important"
                          >
                            {attribute.optionName}
                          </CustomLabel>
                          {index < formattedData.length - 1 && (
                            <CustomLabel fontSize="10px" margin="0px 6px">
                              |
                            </CustomLabel>
                          )}
                        </FlexContainer>
                      </div>
                    ))}
                  </FlexContainer>
                </div>
              )}

              <div>
                <FlexContainer
                  className="product-quantity"
                  margin={
                    formattedData.some((attr) => attr.color)
                      ? "6px 0px 0px"
                      : "12px 0px 0px"
                  }
                >
                  {parseInt(quantity) > 1 && (
                    <img
                      onClick={() =>
                        onIncludedItemsQuantityChange(
                          itemIndex,
                          includedIndex,
                          false,
                        )
                      }
                      src={MinusIcon}
                      alt="Delete Icon"
                    />
                  )}
                  <input type={"text"} value={quantity} />
                  {/*Do not allow user to increase quantity if custom pricing. */}
                  {!price.isCustomPricing() && (
                    <>
                      <img
                        src={Plus}
                        alt="Add"
                        onClick={() =>
                          onIncludedItemsQuantityChange(
                            itemIndex,
                            includedIndex,
                            true,
                          )
                        }
                      />
                      <CustomLabel
                        fontSize="9px !important"
                        color="#556BB8 !important"
                        margin="0px 0px 0px 10px"
                        lineHeight="12px"
                      >
                        Buy additional for $
                        {AccountSettingsStore.formatAmount(
                          priceDescription?.priceInNumber || 0,
                          true,
                        )}{" "}
                        each
                      </CustomLabel>
                    </>
                  )}
                </FlexContainer>
              </div>
            </FlexContainer>
          </div>
          <div>
            <FlexContainer alignItems="center" justifyContent="flex-end">
              <CustomLabel className="discounted-price">
                {parseFloat(quantity) <= 1
                  ? "FREE"
                  : `$${AccountSettingsStore.formatAmount(priceDescription?.subTotal || 0, true)}`}
              </CustomLabel>
            </FlexContainer>
          </div>
        </FlexContainer>
        <CustomLine
          width="100%"
          height="2px"
          bgColor="#ddd"
          margin="14px 0px"
        />
      </>
    );
  };

  return (
    <FlexContainer fxLayout="column">
      <FlexContainer justifyContent="space-between" alignItems="center">
        <CustomLabel fontSize="18px" fontWeight="700" color="#000">
          ITEMS
        </CustomLabel>
        {/* <SingleSelect
          selectedKey={sampleOptions[0].value}
          searchable={false}
          nameKeys={["label"]}
          valueKeys={["value"]}
          options={options || []}
          onChange={() => ""}
          className="dpdn-no-border"
        /> */}
      </FlexContainer>
      <FlexContainer fxLayout="column" spacing={10} margin="33px 0px 4px">
        {clonedItems.map((item, idx) => {
          return (
            <>
              {renderItemDetails(
                idx,
                false,
                item.itemType,
                item.medias,
                item.name,
                item.description,
                item.quantity,
                item.hasVariants,
                item.variants,
                item.selectedVariantIndex,
                item.price,
                item,
              )}
              {item.includedItemsByCondition?.map(
                (includedItem, includedIndex) =>
                  renderIncludedItems(includedItem, idx, includedIndex),
              )}
            </>
          );
        })}

        {addOns.map(
          (item, idx) =>
            !item.isIncludedItem &&
            renderItemDetails(
              idx,
              true,
              item.itemType,
              item.media,
              item.name,
              item.description,
              item.quantity,
              item.hasVariants,
              item.selectedVariant || new ProductAttributeInputModel(),
              item.variantIndex() || 0,
              item,
              undefined,
              item,
            ),
        )}
      </FlexContainer>
    </FlexContainer>
  );
};

export default observer(PurchasePaymentItemSection);
